/**
 * InvoiceRequestRejectController — page /demande-facture-refus.html
 * (EPIC-045 / 45.5).
 *
 * Page dédiée de saisie du motif de rejet d'une `invoice_request_from_qr`
 * (§7.K.E). Convention `feedback_no_modal_dedicated_edit_page`.
 *
 * Au mount : récupère `?id=<uuid>`, charge la demande via
 * `GET /v1/invoice-requests/{id}`, pré-remplit le bloc récap (nom
 * client, email, type, montant, date) en lecture seule.
 *
 * Au submit : `POST /v1/invoice-requests/{id}/reject` avec `{reason}`.
 * Validation : 1 ≤ reason.length ≤ 500 (client ET serveur). Sur succès,
 * redirige vers `/paiements-en-attente.html`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const REASON_MIN_LENGTH = 1;
const REASON_MAX_LENGTH = 500;

const CLIENT_TYPE_LABELS = {
  b2b: "B2B",
  b2c: "B2C",
};

export default class InvoiceRequestRejectController extends BaseController {
  static targets = [
    "reason",
    "errorBanner",
    "confirmBtn",
    "reqSubmittedAt",
    "reqClientName",
    "reqClientEmail",
    "reqClientType",
    "reqClaimedAmount",
    "reqClaimedDate",
  ];

  connect() {
    const qs = new URLSearchParams(window.location.search);
    this._id = qs.get("id") || "";
    if (this._id === "") {
      this._showErrorBanner("Identifiant de la demande manquant dans l'URL.");
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
      return;
    }
    this._loadRequest(this._id);
  }

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  async _loadRequest(id) {
    try {
      const response = await api.invoiceRequests.get(id);
      const req = response?.data ?? null;
      if (!req) {
        this._showErrorBanner("Demande introuvable.");
        if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
        return;
      }
      this._fillRecap(req);
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        this._showErrorBanner("Demande introuvable.");
      } else if (error instanceof APIError && error.message) {
        this._showErrorBanner(error.message);
      } else {
        this._showErrorBanner("Impossible de charger la demande.");
      }
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
    }
  }

  _fillRecap(r) {
    if (this.hasReqSubmittedAtTarget) {
      this.reqSubmittedAtTarget.textContent = this._formatDateTime(r.submittedAt);
    }
    if (this.hasReqClientNameTarget) {
      this.reqClientNameTarget.textContent = r.clientName ?? "—";
    }
    if (this.hasReqClientEmailTarget) {
      this.reqClientEmailTarget.textContent = r.clientEmail ?? "—";
    }
    if (this.hasReqClientTypeTarget) {
      const label = CLIENT_TYPE_LABELS[r.clientType] ?? (r.clientType || "—").toUpperCase();
      this.reqClientTypeTarget.textContent = label;
    }
    if (this.hasReqClaimedAmountTarget) {
      this.reqClaimedAmountTarget.textContent = this._formatAmount(r.claimedAmount);
    }
    if (this.hasReqClaimedDateTarget) {
      this.reqClaimedDateTarget.textContent = this.formatShortDate(r.claimedDate);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async onSubmit(event) {
    if (event) event.preventDefault();
    if (this._id === "") return;
    if (!this.hasReasonTarget) return;
    const reason = this.reasonTarget.value.trim();
    if (reason.length < REASON_MIN_LENGTH) {
      this._showErrorBanner("Le motif est obligatoire.");
      this.reasonTarget.setAttribute("aria-invalid", "true");
      this.reasonTarget.focus();
      return;
    }
    if (reason.length > REASON_MAX_LENGTH) {
      this._showErrorBanner(`Le motif est limité à ${REASON_MAX_LENGTH} caractères.`);
      this.reasonTarget.setAttribute("aria-invalid", "true");
      return;
    }
    this._hideErrorBanner();
    this.reasonTarget.removeAttribute("aria-invalid");
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
    try {
      await api.invoiceRequests.reject(this._id, reason);
      window.location.assign("/paiements-en-attente.html");
    } catch (error) {
      if (error instanceof APIError && error.message) {
        this._showErrorBanner(error.message);
      } else {
        this._showErrorBanner("Erreur réseau — impossible de rejeter la demande.");
      }
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatAmount(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "—";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  _formatDateTime(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  }

  _showErrorBanner(message) {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.hidden = true;
    this.errorBannerTarget.textContent = "";
  }
}
